import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { plusWidgetArticleHomeV2$key } from "scmp-app/queries/__generated__/plusWidgetArticleHomeV2.graphql";

import { Container, CoverLink, Headline, InlineCover, Section, Timestamp } from "./styles";

type Props = {
  className?: string;
  reference: plusWidgetArticleHomeV2$key;
  variant?: Variant;
};

type Variant = "cover" | "lead" | "text";

export const HomeV2PlusWidgetArticle: FunctionComponent<Props> = ({
  className,
  reference,
  variant = "text",
}) => {
  const entity = useFragment(
    graphql`
      fragment plusWidgetArticleHomeV2 on Content {
        entityId
        urlAlias
        ...entityLink
        ...hooksContentItemProvidersContent
          @arguments(
            withCoverImage: true
            withCoverImageSize500x500: true
            withCoverImageSize540x360: true
            withHeadline: true
            withPublishedDate: true
            withSections: true
          )
      }
    `,
    reference,
  );
  const providers = useContentItemProviders(entity);

  const linkQuery = { display: "plus", module: "content_discovery", pgtype: "homepage" };

  const handleItemClickEvent = () => {
    sendGA4Tracking({
      action: "click",
      category: "recirculation",
      customized_parameters: {
        article_id: entity.entityId ?? "",
        destination_url: entity.urlAlias ?? "",
        page_type: "homepage",
        widget_name: "scmp_plus",
      },
      subcategory: "widget",
    });
  };

  const coverImage = (
    <CoverLink onClick={handleItemClickEvent} query={linkQuery} reference={entity}>
      {providers.coverImage({
        responsiveVariants: {
          desktopUp: "size500x500",
          mobileUp: "size540x360",
          tabletUp: "size500x500",
        },
      })}
    </CoverLink>
  );

  if (variant === "cover") return coverImage;

  return (
    <Container className={className}>
      <Section>{providers.sections()}</Section>
      <Headline onClick={handleItemClickEvent} query={linkQuery} reference={entity}>
        {providers.headline({ hideTooltip: true, preferSocialHeadline: true })}
      </Headline>
      <Timestamp>{providers.publishedDate({ displayMode: "relative" })}</Timestamp>
      {variant === "lead" && <InlineCover>{coverImage}</InlineCover>}
    </Container>
  );
};

HomeV2PlusWidgetArticle.displayName = "HomeV2PlusWidgetArticle";
